import { MenuProps } from 'antd';
import {
  ATable,
  MenuOptions,
  StatusActive,
  DropdownOptions,
  PageIntro,
  PageLayout,
  PagePanel,
} from '../../components';
import {
  AreaChartOutlined,
  EditOutlined,
  FileDoneOutlined,
  PlusCircleOutlined,
  RedoOutlined,
  RiseOutlined,
  PrinterFilled,
} from '@ant-design/icons';
import { useToast } from '../../hooks/toast';
import { RiHealthBookLine } from 'react-icons/ri';
import { useNavigate, useParams } from 'react-router-dom';
import { useCallback, useEffect, useState } from 'react';
import api from '../../services/api';
import {
  IClient,
  IForm,
  IModelForm,
  IProfessional,
} from '../../shared/interfaces';
import FormClientCreateEdit from './CreateEdit';
import { useAuth } from '../../hooks/auth';
import { PermissionActions } from '../../shared/enums/permissions.enum';

const Forms: React.FC = () => {
  const { client_id } = useParams();
  const { addToast } = useToast();
  const navigate = useNavigate();
  const { permissionFind } = useAuth();
  const [search, setSearch] = useState<string>('');
  const [clientForms, setClientForms] = useState<IForm[]>([]);
  const [modelForms, setModelForms] = useState<IModelForm[]>([]);
  const [clients, setClients] = useState<IClient[]>([]);
  const [professionals, setProfessionals] = useState<IProfessional[]>([]);
  const [clientForm, setClientForm] = useState<IForm | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [modalCreate, setModalCreate] = useState<boolean>(false);
  const [limit, setLimit] = useState<number>(10);
  const [page, setPage] = useState<number>(1);
  const [pages, setPages] = useState<number>();

  const listClientForms = useCallback(async () => {
    setLoading(true);
    setClientForms([]);
    try {
      const response = await api.post('/clients-forms/list', {
        client_id: client_id,
        take: limit,
        skip: (page - 1) * limit,
        keyword: search,
      });
      setPages(response.data.count);
      setClientForms(response.data.data);
    } catch (err) {
      addToast({
        type: 'error',
        title: 'Erro',
        description: 'Erro ao listar prontuários',
      });
    } finally {
      setLoading(false);
    }
  }, [limit, search, page, addToast]);

  const listProfessionals = useCallback(async () => {
    try {
      const response = await api.get(`/professionals/select`);
      setProfessionals(response.data);
    } catch (err) {
      addToast({
        type: 'error',
        title: 'Erro',
        description: 'Erro ao listar os profissionais',
      });
    }
  }, [addToast]);

  const listClients = useCallback(async () => {
    await api
      .get(`/clients/select/${1}`)
      .then((response) => {
        setClients(response.data);
      })
      .catch((error) => {
        addToast({
          type: 'error',
          title: 'Erro',
          description: `${error.response.data.message}`,
        });
      });
  }, [addToast]);

  const listModelForms = useCallback(async () => {
    try {
      const response = await api.get(`/forms/select`);
      setModelForms(response.data);
    } catch (err) {
      addToast({
        type: 'error',
        title: 'Erro',
        description: 'Erro ao listar os profissionais',
      });
    }
  }, [addToast]);

  const renderMenuOptions = () => {
    const options: MenuProps['items'] = [];
    options.push({
      label: 'Atualizar',
      key: '1',
      icon: <RedoOutlined />,
      onClick: () => listClientForms(),
    });
    if (!client_id) {
      if (permissionFind([PermissionActions.formsCreate])) {
        options.push({
          label: 'Cadastrar',
          key: '2',
          icon: <PlusCircleOutlined />,
          onClick: () => {
            setClientForm(null);
            setModalCreate(true);
          },
        });
      }
    }
    if (client_id) {
      options.push({
        label: 'Gráficos Gerais',
        key: '3',
        icon: <AreaChartOutlined />,
        onClick: () => navigate(`/forms/${client_id}/charts`),
      });
    }
    return options;
  };

  const renderModalFormCreateEdit = () => {
    if (modalCreate)
      return (
        <FormClientCreateEdit
          open={modalCreate}
          close={() => setModalCreate(false)}
          clientForm={clientForm}
          setClientForms={setClientForms}
          clients={clients}
          professionals={professionals}
          modelForms={modelForms}
        />
      );
  };

  useEffect(() => {
    listClients();
    listProfessionals();
    listModelForms();
  }, []);

  useEffect(() => {
    listClientForms();
  }, [page, search, limit, listClientForms]);

  return (
    <PageLayout>
      <PageIntro
        badge="Prontuários"
        icon={<RiHealthBookLine />}
        title="Prontuários"
        description="Acesse registros clínicos, evoluções e respostas dos formulários com uma navegação mais clara para a equipe."
      />
      <MenuOptions
        menu_items={renderMenuOptions()}
        showButtonBack={!!client_id}
        showInputSearch
        search={search}
        setSearch={setSearch}
      />
      <PagePanel>
        <ATable
          columns={[
            {
              title: 'Id',
              width: 100,
              dataIndex: 'code',
            },
            {
              title: 'Cliente',
              dataIndex: 'client_name',
            },
            {
              title: 'Profissional',
              dataIndex: 'professional_name',
            },
            {
              title: 'Modelo Prontuário',
              dataIndex: 'title_form',
            },
            {
              title: 'Status',
              dataIndex: 'status',
              width: 95,
              render: (value: boolean) => <StatusActive status={value} />,
            },
            {
              title: 'Opções',
              width: 130,
              render: (cell: IForm) => (
                <DropdownOptions
                  items={[
                    permissionFind([PermissionActions.formsUpdate])
                      ? {
                          label: 'Editar',
                          key: '1',
                          icon: <EditOutlined />,
                          onClick: () => {
                            setClientForm(cell);
                            setModalCreate(true);
                          },
                        }
                      : null,
                    permissionFind([PermissionActions.formsQuiz])
                      ? {
                          label: 'Responder Prontuário',
                          key: '2',
                          icon: <FileDoneOutlined />,
                          onClick: () => {
                            navigate(`/form/${cell.id}/quiz`);
                          },
                        }
                      : null,
                    permissionFind([PermissionActions.formsUpdate])
                      ? {
                          label: 'Evolução',
                          key: '3',
                          icon: <RiseOutlined />,
                          onClick: () => {
                            navigate(`/form/${cell.id}/evolution`);
                          },
                        }
                      : null,
                    {
                      label: 'Imprimir',
                      key: '4',
                      icon: <PrinterFilled />,
                      onClick: () => {
                        navigate(`/form/${cell.id}/print`);
                      },
                    },
                  ]}
                />
              ),
            },
          ]}
          loading={loading}
          dataSource={clientForms}
          page={page}
          setPage={setPage}
          pages={pages}
          limit={limit}
          setLimit={setLimit}
        />
      </PagePanel>
      {renderModalFormCreateEdit()}
    </PageLayout>
  );
};

export default Forms;
